import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import type {
  BulkAddJobItemToCartBody,
  CartCheckoutBody,
  UpsertAddressBody,
} from 'src/openapi';
import { getFormattedErr } from 'src/helpers';
import apiClient from 'src/helpers/apiClient';

export const fetchPostedJobs = createAsyncThunk(
  'premium/get-jobs',
  async (
    {
      query,
      page_no,
      page_size,
      orgId,
      jobStatus,
      isGptJob,
      planTypeList,
    }: {
      query: string;
      page_no: number;
      page_size: number;
      orgId: string;
      jobStatus?: string;
      isGptJob?: boolean;
      planTypeList?: string[];
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobPostedList(
        {
          isGptJob,
          jobStatus,
          orgId,
          pageNo: page_no,
          pageSize: page_size,
          planTypeList,
          query,
        },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const addToCart = createAsyncThunk(
  'premium/add-to-cart',
  async (
    { orgId, payload }: { orgId: string; payload: BulkAddJobItemToCartBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiAddJobItemsToCart({ orgId, bulkAddJobItemToCartBody: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchUserPaymentAddress = createAsyncThunk(
  'premium/get--user-payment-address',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetUserAddressDetails();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updatePaymentAddress = createAsyncThunk(
  'premium/update-payment-address',
  async (payload: UpsertAddressBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiUpsertUserAddressDetails({ upsertAddressBody: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const paymentFinish = createAsyncThunk(
  'premium/payment-finish',
  async ({ obj }: any, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiCompleteTransaction(obj);
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const paymentCancel = createAsyncThunk(
  'premium/payment-cancel',
  async ({ obj }: any, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiCancelTransaction(obj);
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const cartCheckout = createAsyncThunk(
  'premium/cart-checkout',
  async (payload: CartCheckoutBody, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiMakePayment({ cartCheckoutBody: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCartItems = createAsyncThunk(
  'premium/get-cart-items',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetCart();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getJobsStoreItems = createAsyncThunk(
  'get/jobs-store-item',
  async ({ couponCode }: { couponCode?: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getPaymentApi()
      ).paymentApiGetAllJobItems({ couponCode });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getPlanById = createAsyncThunk(
  'premium/plan/{plan_id}',
  async ({ planId }: { planId: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await apiClient.getOrgApi()
      ).organizationApiGetPlanById({ planId });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
